// @flow

import * as React from 'react';

import { useField, useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
// $FlowFixMe[missing-export]
import { lruMemoize } from 'reselect';

import type { ProductData } from '@gandi/cart';
import {
  CheckboxField,
  TextAreaFieldGroup,
  InputTextFieldGroup,
  useBasicValidators,
  composeValidators,
  debounce,
} from '@gandi/form/FinalForm';
import { useTranslator, mapTags } from '@gandi/translate';

import {
  Alert,
  Base,
  Block,
  BlockContent,
  BlockHeading,
  UnorderedList,
  TypoListItem as ListItem,
} from '@gandi/ui';

import {
  CERT_ALTNAMES_INPUT_NAME,
  CERT_CN_INPUT_NAME,
  CERT_CSR_INPUT_NAME,
  CERT_DCV_METHOD_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  CERT_AUTO_DCV_VALID,
  CERT_PROVIDER_INPUT_NAME,
  CERT_APEX_AND_WWW,
  CERT_TYPE_INPUT_NAME,
} from '../../consts';

import { hasApexOnlyField } from '../../form/mapper';
import {
  validateCSR,
  validateCertificateOptions,
} from '../../modules/certificate';

import type { ValidateCSRError } from '../../modules/certificate';
import type { CertificateApiResponse, FormValues } from '../../types';

import cnStyles from './CNOption.scss';
import styles from './CSROption.scss';

/**
 * Timeout before starting to validate a copy/pasted field
 */
const DEBOUNCE_TIMEOUT_PASTE = 500;

// csr are about 17 lines, 14 rows is about 310px which visualy «fine»
const csrProps = { className: styles.csrTextArea, rows: 14 };
const cnProps = { className: cnStyles.input };

type Props = {
  certToRenew?: CertificateApiResponse,
  formDataMapper: (formValues: FormValues) => void | ProductData,
};
export function CSROption({
  certToRenew,
  formDataMapper,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const form = useForm<FormValues>();
  const {
    input: { value: protect },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_PROTECT_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: altnames },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_ALTNAMES_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: provider },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_PROVIDER_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: type },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_INPUT_NAME, { subscription: { value: true } });

  const [hasDetectedSAN, setHasDetectedSAN] = React.useState<boolean>(false);

  const { required } = useBasicValidators();
  const helpCSR = mapTags(
    __(
      'Refer to our [[onlineDocumentation:]] if you would like more information on this process.',
    ),
    () => ({
      onlineDocumentation: (
        <a
          href={__('https://docs.gandi.net/en/ssl/common_operations/csr.html')}
          target="_blank"
          rel="noopener noreferrer"
        >
          {__('online documentation')}
        </a>
      ),
    }),
  );

  const helpMap = {
    single: (
      <UnorderedList gap="xxsmall">
        <ListItem>
          {__('bare domain name = it secures example.com + www.example.com or')}
        </ListItem>
        <ListItem>
          {__('www subdomain = it secures www.example.com + example.com')}
        </ListItem>
      </UnorderedList>
    ),
    multi: (
      <>
        <Base tagName="p" marginTop="none" marginBottom="xsmall">
          {__('Each domain or subdomain you want to cover must be the CN.')}
        </Base>
        <UnorderedList gap="xxsmall">
          <ListItem>
            {__('bare domain name = will only secure the bare domain')}
          </ListItem>
          <ListItem>{__('subdomain = only secure the sub domain')}</ListItem>
        </UnorderedList>
      </>
    ),
    wildcard: (
      <Base tagName="p" margin="none">
        {__(
          'The common name should be *.example.com = it will also secure the bare domain.',
        )}
      </Base>
    ),
  };

  const helpCN = helpMap[protect];
  const showApexChoice = hasApexOnlyField({
    provider,
    protect,
    type,
  });

  /**
   * If the CSR is valid, fill the CN field and detect if auto dcv can be used.
   * If not, return the field's error.
   *
   * This function use memoization (function is not recreated on each render) to
   * prevent re-rendering.
   */
  const csrValidator = React.useMemo<(?string) => Promise<string | void>>(
    () =>
      /**
       * This function is debounced, it means the validator can be called
       * several times in a second (deleting with keyboard for example), it will
       * only be called one time.
       */
      debounce<string | void>(
        /**
         * /!\ beware that it's not a good idea to change a field in a validator
         * because it might retrigger an other fields validation spool and end
         * up in a loop:
         *
         * All validators are executed everytime a displayed field is modified
         * unless `validateFields` is provided. Note that `FinalFormArray` does
         * not support this feature and always submit the event to all active
         * validators.
         *
         * That's why the validator function is memoized (a.k.a «pure function»)
         * because it includes effects on the form and we only want to apply
         * these effects when `csr` has changed. In other words, each time the
         * validation is called it once apply effects then never again until
         * `csr` has been modified.
         */
        lruMemoize(async csr => {
          try {
            const validation = await dispatch(
              validateCSR({ csr }, certToRenew),
            );

            const cn = validation.fqdns[0];

            // ---
            // We try to detect if auto dcv can be applied so we let user choose
            // his dcv himself in the next step (we could do this in form
            // subscription but it's probably better to wait for the csr to be
            // validated and then do batch changes on form values).
            let isAutoDcvValid = false;
            try {
              const formValues = form.getState().values;
              const productData = formValues
                ? formDataMapper({
                    ...formValues,
                    [CERT_DCV_METHOD_INPUT_NAME]: 'auto',
                  })
                : undefined;
              if (productData) {
                const errors = await dispatch(
                  validateCertificateOptions(productData),
                );

                if (errors.length === 0) {
                  isAutoDcvValid = true;
                }
              }
            } catch (e) {
              // this not a blocking error, it will eventually be retried on submit
            }
            // ---

            form.batch(() => {
              form.change(CERT_CN_INPUT_NAME, cn);

              if (isAutoDcvValid) {
                form.change(CERT_DCV_METHOD_INPUT_NAME, 'auto');
                form.change(CERT_AUTO_DCV_VALID, true);
              } else {
                form.change(CERT_DCV_METHOD_INPUT_NAME, 'dns');
                form.change(CERT_AUTO_DCV_VALID, false);
              }

              if (validation.altnames.length === 0) {
                setHasDetectedSAN(false);
              }

              // We detect SANs and prefil form input
              if (
                validation.altnames.length > 0 &&
                // altnames is a string except for multi protect
                (!Array.isArray(altnames) ||
                  altnames.filter(Boolean).length === 0)
              ) {
                if (protect === 'multi') {
                  form.change(CERT_ALTNAMES_INPUT_NAME, validation.altnames);
                } else {
                  // only display alert when type is not multi
                  setHasDetectedSAN(true);
                }
              }
            });
            return undefined;
          } catch (err) {
            if (form.getFieldState(CERT_CN_INPUT_NAME)?.value !== undefined) {
              form.change(CERT_CN_INPUT_NAME, undefined); // note that this has no effects when there is a `certToRenew`
            }

            // XXX: eventually improve with specific error class
            if (err instanceof Error) {
              switch (err.message) {
                // when renewing a certificate, the CN of the CSR has to equal
                // to the CN of the certificate being renewed
                case 'CN mismatch':
                  // $FlowFixMe[incompatible-call] cn & certToRenewCN might be undefined
                  return __(
                    'The fqdn of your CSR (%(cn)s) has to be %(certToRenewCN)s.',
                    {
                      cn: (err as ValidateCSRError).cn,
                      certToRenewCN: (err as ValidateCSRError).certToRenewCN,
                    },
                  );
                default:
                  return err.message || __('CSR is invalid');
              }
            }

            // error has been parsed: it list errors for any fields found by parseApiErrors
            // XXX: retrieve on csr field & dispatch other errors into respective fields
            return Object.values(err);
          }
        }),
        DEBOUNCE_TIMEOUT_PASTE,
      ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [certToRenew],
  );

  return (
    <>
      <Block marginBottom={hasDetectedSAN ? 'small' : undefined}>
        <BlockHeading>{__('CSR')}</BlockHeading>
        <BlockContent>
          <Base marginBottom="small">
            <TextAreaFieldGroup
              name={CERT_CSR_INPUT_NAME}
              placeholder={__('Paste your CSR here')}
              validate={composeValidators(required, csrValidator)}
              // Makes sure san are validated too if it has been set before this field
              validateFields={
                protect === 'multi' ? [CERT_ALTNAMES_INPUT_NAME] : undefined
              }
              controlPropsExtra={csrProps}
            />
          </Base>
          {showApexChoice && (
            <Base marginTop="none" marginBottom="small">
              <CheckboxField name={CERT_APEX_AND_WWW}>
                {__(
                  'Include both example.com and www.example.com in the certificate',
                )}
              </CheckboxField>
            </Base>
          )}
          <Base tagName="p" marginTop="none" marginBottom="small">
            {helpCSR}
          </Base>
          {!certToRenew && (
            <Base marginBottom="small">
              <InputTextFieldGroup
                name={CERT_CN_INPUT_NAME}
                placeholder={__('Waiting for CSR...')}
                readOnly
                validate={required}
                validateFields={[]}
                controlPropsExtra={cnProps}
              />
            </Base>
          )}
          {!showApexChoice && helpCN}
        </BlockContent>
      </Block>
      {hasDetectedSAN && (
        <Alert
          message={__(
            'We have detected alternative names (SAN) in your CSR. Please choose Several addresses option, if you want to secure them all (only available for Standard or Business certificates)',
          )}
          intent="warning"
        />
      )}
    </>
  );
}
